import * as React from 'react'
import { Link, useParams } from 'react-router'
import { Loader2 } from 'lucide-react'
import { newPasswordSchema } from '@lib/input/tenant-auth.input'
import { linkTokenParamSchema } from '@lib/validators/params'
import { AuthShell } from '@/components/AuthShell'
import { ErrorState } from '@/components/PageState'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { PASSWORD_RESET_AUDIENCES, type PasswordResetAudience } from '@/auth/passwordResetAudience'

/** Choose a new password from an emailed link. Signs out every other session. */
export function ResetPasswordPage({ audience = 'dashboard' }: { audience?: PasswordResetAudience }) {
  const target = PASSWORD_RESET_AUDIENCES[audience]
  const { token: rawToken } = useParams()
  const parsedToken = linkTokenParamSchema.safeParse(rawToken)
  const token = parsedToken.success ? parsedToken.data : null
  const [password, setPassword] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [done, setDone] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    const parsed = newPasswordSchema.safeParse(password)
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Choose a longer password.')
      return
    }
    if (password !== confirm) {
      setError("The passwords don't match.")
      return
    }
    if (token === null) return
    setIsSubmitting(true)
    try {
      await target.confirm({ token, password: parsed.data })
      setDone(true)
    } catch (failure) {
      setError(apiErrorMessage(failure, 'This reset link is invalid or has expired.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (token === null) {
    return (
      <AuthShell title="Choose a new password" description="At least 12 characters.">
        <ErrorState
          message="This reset link is invalid or has expired."
          action={
            <Link to={target.forgotPath} className="font-medium underline underline-offset-4">
              Request a new link
            </Link>
          }
        />
      </AuthShell>
    )
  }

  if (done) {
    return (
      <AuthShell title="Password changed">
        <p className="text-body text-muted-foreground">
          Sign in with your new password. Any other signed-in sessions have been signed out.
        </p>
        <Button asChild className="mt-4">
          <Link to={target.loginPath}>Sign in</Link>
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Choose a new password" description="At least 12 characters.">
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Field label="New password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <Field label="Confirm password" htmlFor="confirm">
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>
        {error ? (
          <ErrorState
            message={error}
            action={
              <Link to={target.forgotPath} className="font-medium underline underline-offset-4">
                Request a new link
              </Link>
            }
          />
        ) : null}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : null}
          Save password
        </Button>
      </form>
    </AuthShell>
  )
}
